import React, { useState, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  TextField,
  MenuItem,
  Switch,
  FormControlLabel,
  Typography,
  Box,
  Divider,
  Checkbox,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { RootStateOrAny, useSelector, useDispatch } from 'react-redux';
import useStyles from './styles';
import ConfirmationDialog from '../ConfirmationDialog/ConfirmationDialog';
import FileUploadSection from './FileUploadSection';
import NotFoundRestaurantsDialog from './NotFoundRestaurantsDialog';
import { snackbarShowMessage } from '../../redux/actions/snackbarActions';
import { getSingleAttribute } from '../../redux/actions/focusedAttributeActions';
import { massUpdateAttribute } from '../../redux/actions/attributesActions';
import Attribute from '../../interfaces/attribute';
import { AppDispatch } from '../../redux/store';
import { useTranslation } from 'react-i18next';

interface AttributesMassUpdateProps {
  open: boolean;
  onClose: () => void;
}

const AttributesMassUpdate: React.FC<AttributesMassUpdateProps> = ({
  open,
  onClose,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();

  const { attributeList: attributes } = useSelector(
    (state: RootStateOrAny) => state.AttributesReducer,
  );

  const attributesData = useMemo(
    () => (attributes?.data || []).filter(attr => attr.status === 'COMPLETED'),
    [attributes],
  );

  // State management
  const [selectedAttributeId, setSelectedAttributeId] = useState<string>('');
  const [selectedAttributeDetails, setSelectedAttributeDetails] =
    useState<Attribute | null>(null);
  const [toggleValue, setToggleValue] = useState<boolean>(false);
  const [enumValueId, setEnumValueId] = useState<number | null>(null);
  const [restaurantNumbers, setRestaurantNumbers] = useState<string[]>([]);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [restaurantMassUpdateResult, setRestaurantMassUpdateResult] = useState<{
    message: string;
    restaurants: string[];
    totalRestaurants?: number;
  } | null>(null);
  const [disableEverywhereElse, setDisableEverywhereElse] =
    useState<boolean>(false);
  const [isDisableConfirmOpen, setIsDisableConfirmOpen] =
    useState<boolean>(false);

  const isToggleAttribute = selectedAttributeDetails?.isToggle || false;
  const enumValues = selectedAttributeDetails?.enumValues || [];

  // Fetch full attribute details when an attribute is selected
  useEffect(() => {
    const fetchAttributeDetails = async () => {
      if (selectedAttributeId) {
        try {
          await dispatch(getSingleAttribute(Number(selectedAttributeId)));
        } catch (error) {
          dispatch(
            snackbarShowMessage(
              t('attributeMassUpdate.errorFetchingAttributeDetails'),
            ),
          );
        }
      }
    };

    fetchAttributeDetails();
  }, [selectedAttributeId, dispatch]);

  // Get fetched attribute details from FocusedAttributeReducer
  const focusedAttributeData = useSelector(
    (state: RootStateOrAny) => state.FocusedAttributeReducer.data,
  );

  // Update local state when focusedAttributeData changes and matches selected attribute
  useEffect(() => {
    if (
      focusedAttributeData &&
      String(focusedAttributeData.id) === selectedAttributeId
    ) {
      setSelectedAttributeDetails(focusedAttributeData);
    }
  }, [focusedAttributeData, selectedAttributeId]);

  // Handle attribute selection
  const handleAttributeChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setSelectedAttributeId(event.target.value);
    setSelectedAttributeDetails(null);
    setToggleValue(false);
    setEnumValueId(null);
  };

  // Handle toggle change
  const handleToggleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setToggleValue(event.target.checked);
  };

  // Handle enum change
  const handleEnumChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEnumValueId(Number(event.target.value));
  };

  // Handle disable everywhere else checkbox
  const handleDisableEverywhereElseChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (event.target.checked) {
      setIsDisableConfirmOpen(true);
    } else {
      setDisableEverywhereElse(false);
    }
  };

  // Handle confirm disable everywhere else
  const handleConfirmDisableEverywhere = () => {
    setDisableEverywhereElse(true);
    setIsDisableConfirmOpen(false);
  };

  // Handle cancel disable everywhere else
  const handleCancelDisableEverywhere = () => {
    setDisableEverywhereElse(false);
    setIsDisableConfirmOpen(false);
  };

  // Handle restaurants extracted from file
  const handleRestaurantsExtracted = (restaurants: string[]) => {
    setRestaurantNumbers(restaurants);
  };

  // Handle file upload error
  const handleFileUploadError = (message: string) => {
    dispatch(snackbarShowMessage(message));
  };

  // Handle reset
  const handleReset = () => {
    setSelectedAttributeId('');
    setSelectedAttributeDetails(null);
    setToggleValue(false);
    setEnumValueId(null);
    setRestaurantNumbers([]);
    setDisableEverywhereElse(false);
  };

  // Handle perform mass update by opening the confirmation dialog
  const handlePerformMassUpdate = () => {
    setIsConfirmDialogOpen(true);
  };

  // Handle confirmation
  const handleConfirmMassUpdate = async () => {
    // Prepare mass update data
    const massUpdateData = {
      attributeId: Number(selectedAttributeId),
      restaurantNumbers,
      ...(isToggleAttribute ? { toggleValue } : { enumValueId }),
      ...(disableEverywhereElse && { disableEverywhereElse: true }),
    };

    try {
      const response = (await dispatch(
        massUpdateAttribute(massUpdateData),
      )) as unknown as {
        data: {
          message: string;
          notFoundRestaurantNumbers: string[];
          totalRestaurants?: number;
        };
      };

      if (!response?.data) {
        setIsConfirmDialogOpen(false);
        return;
      }

      const { notFoundRestaurantNumbers, totalRestaurants } = response.data;

      setIsConfirmDialogOpen(false);

      // Handle restaurants not found case
      if (notFoundRestaurantNumbers && notFoundRestaurantNumbers.length > 0) {
        setRestaurantMassUpdateResult({
          message: t('attributeMassUpdate.massUpdateToRestaurantSuccess'),
          restaurants: notFoundRestaurantNumbers,
          totalRestaurants,
        });
        return;
      }

      // Handle success case - all restaurants found
      dispatch(snackbarShowMessage(t('attributeMassUpdate.massUpdateSuccess')));
      handleReset();
      onClose();
    } catch (error) {
      setIsConfirmDialogOpen(false);

      // Extract error description from response
      const errorResponse = (
        error as {
          response?: {
            data?: { error_description?: string; error_code?: string };
          };
        }
      )?.response?.data;
      const errorDescription =
        errorResponse?.error_description ||
        errorResponse?.error_code ||
        t('attributeMassUpdate.massUpdateError');

      dispatch(snackbarShowMessage(errorDescription));
    }
  };

  // Handle close of not found restaurants dialog
  const handleCloseNotFoundDialog = () => {
    setRestaurantMassUpdateResult(null);
    handleReset();
    onClose();
  };

  const handleCloseConfirmDialog = () => {
    setIsConfirmDialogOpen(false);
  };

  // Check if form is valid
  const isFormValid = useMemo(() => {
    const hasAttribute = !!selectedAttributeId;
    const hasValue = isToggleAttribute || !!enumValueId;
    const hasRestaurants = restaurantNumbers.length > 0;
    return hasAttribute && hasValue && hasRestaurants;
  }, [selectedAttributeId, isToggleAttribute, enumValueId, restaurantNumbers]);

  // Prepare confirmation message
  const confirmationMessage = useMemo(() => {
    if (!selectedAttributeDetails) return '';

    const attributeName = selectedAttributeDetails.name;

    // Get display value
    let displayValue = '';
    if (isToggleAttribute) {
      displayValue = toggleValue
        ? t('attributeMassUpdate.attributeEnabled')
        : t('attributeMassUpdate.attributeDisabled');
    } else {
      const selectedEnum = enumValues.find(ev => ev.id === enumValueId);
      displayValue = selectedEnum?.name || '';
    }

    const restaurantsList = restaurantNumbers.slice(0, 5).join(', ');
    const moreRestaurants =
      restaurantNumbers.length > 5
        ? ` and ${restaurantNumbers.length - 5} more...`
        : '';

    return (
      <>
        <Typography variant="body1" className={classes.warningText}>
          <strong>{t('attributeMassUpdate.actionWarning')}</strong>
        </Typography>
        <Typography variant="body2">
          {t('attributeMassUpdate.actionConfirmMessage')}:{' '}
          <strong>{attributeName}</strong> <strong>&rarr;</strong>{' '}
          <strong>{displayValue}</strong>
        </Typography>
        <Typography variant="body2" className={classes.restaurantListText}>
          {restaurantsList}
          {moreRestaurants}
        </Typography>
        <Typography variant="body2" className={classes.summaryText}>
          {t('attributeMassUpdate.totalRestaurants')}:{' '}
          <strong>{restaurantNumbers.length}</strong>
        </Typography>
        {disableEverywhereElse && (
          <Typography variant="body2" className={classes.disableWarningText}>
            {t('attributeMassUpdate.disableEverywhereElseWarning')}
          </Typography>
        )}
      </>
    );
  }, [
    selectedAttributeDetails,
    isToggleAttribute,
    toggleValue,
    enumValueId,
    enumValues,
    restaurantNumbers,
    disableEverywhereElse,
    classes,
    t,
  ]);

  const handleModalClose = (reason: string) => {
    if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
      onClose();
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={(event, reason) => handleModalClose(reason)}
        maxWidth="md"
        fullWidth
        className={classes.dialog}
      >
        <DialogTitle>
          <Typography className={classes.headerTitle}>
            {t('attributeMassUpdate.modalLabel')}
          </Typography>
          <Typography className={classes.headerSubtitle}>
            {t('attributeMassUpdate.modalSubLabel')}
          </Typography>
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent className={classes.content}>
          {/* Attribute Selection */}
          <Box className={classes.section}>
            <Typography variant="subtitle1" className={classes.sectionTitle}>
              {t('attributeMassUpdate.selectAttribute')}
            </Typography>
            <TextField
              select
              fullWidth
              variant="outlined"
              value={selectedAttributeId}
              onChange={handleAttributeChange}
              placeholder={t('attributeMassUpdate.chooseAttribute')}
              className={classes.textField}
            >
              <MenuItem value="">
                <em>{t('attributeMassUpdate.selectAttribute')}</em>
              </MenuItem>
              {attributesData.map(attr => (
                <MenuItem key={attr.id} value={String(attr.id)}>
                  {attr.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          {/* Attribute Value */}
          {selectedAttributeId && (
            <Box className={classes.section}>
              <Typography variant="subtitle1" className={classes.sectionTitle}>
                {t('attributeMassUpdate.attributeValue')}
              </Typography>
              {isToggleAttribute ? (
                <Box className={classes.toggleContainer}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={toggleValue}
                        onChange={handleToggleChange}
                        color="primary"
                      />
                    }
                    label={
                      toggleValue
                        ? t('attributeMassUpdate.attributeEnabled')
                        : t('attributeMassUpdate.attributeDisabled')
                    }
                    labelPlacement="start"
                    className={classes.toggleLabel}
                  />
                </Box>
              ) : (
                <TextField
                  select
                  fullWidth
                  variant="outlined"
                  value={enumValueId || ''}
                  onChange={handleEnumChange}
                  placeholder={t('attributeMassUpdate.chooseValue')}
                  className={classes.textField}
                >
                  <MenuItem value="">
                    <em>{t('attributeMassUpdate.selectAttribute')}</em>
                  </MenuItem>
                  {enumValues.map((enumItem, index) => (
                    <MenuItem key={index} value={enumItem.id}>
                      {enumItem.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </Box>
          )}

          {/* File Upload */}
          <FileUploadSection
            onRestaurantsExtracted={handleRestaurantsExtracted}
            onError={handleFileUploadError}
          />

          {/* Disable Everywhere Else Checkbox */}
          {restaurantNumbers.length > 0 && (
            <Box className={classes.section}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={disableEverywhereElse}
                    onChange={handleDisableEverywhereElseChange}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2" className={classes.checkboxLabel}>
                    {t('attributeMassUpdate.disableEverywhereElseLabel')}
                  </Typography>
                }
                className={classes.checkboxContainer}
              />
            </Box>
          )}
        </DialogContent>
        <Divider />
        <DialogActions className={classes.actions}>
          <Button
            variant="outlined"
            onClick={handleReset}
            className={classes.resetButton}
          >
            {t('attributeMassUpdate.reset')}
          </Button>
          <Button
            variant="outlined"
            onClick={handlePerformMassUpdate}
            disabled={!isFormValid}
            className={classes.submitButton}
          >
            {t('attributeMassUpdate.performMassUpdate')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={isConfirmDialogOpen}
        onClose={handleCloseConfirmDialog}
        onConfirmation={handleConfirmMassUpdate}
        title={t('attributeMassUpdate.confirmMassUpdate')}
        confirmText={t('attributeMassUpdate.confirm')}
      >
        {confirmationMessage}
      </ConfirmationDialog>

      {/* Not Found Restaurants Dialog */}
      {restaurantMassUpdateResult && (
        <NotFoundRestaurantsDialog
          open={!!restaurantMassUpdateResult}
          message={restaurantMassUpdateResult.message}
          restaurants={restaurantMassUpdateResult.restaurants}
          totalRestaurants={restaurantMassUpdateResult.totalRestaurants}
          onClose={handleCloseNotFoundDialog}
        />
      )}

      {/* Disable Everywhere Else Confirmation Dialog */}
      <Dialog
        open={isDisableConfirmOpen}
        onClose={handleCancelDisableEverywhere}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography className={classes.headerTitle}>
            {t('attributeMassUpdate.confirmDisableTitle')}
          </Typography>
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={handleCancelDisableEverywhere}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Typography variant="body1">
            {t('attributeMassUpdate.confirmDisableMessage')}
          </Typography>
        </DialogContent>
        <Divider />
        <DialogActions className={classes.actions}>
          <Button
            variant="outlined"
            onClick={handleCancelDisableEverywhere}
            className={classes.resetButton}
          >
            {t('app.common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmDisableEverywhere}
            className={classes.dangerButton}
          >
            {t('app.common.ok')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AttributesMassUpdate;
